
import { StatsCard } from '@/components/shared/StatsCard'
import { StatsChart } from '@/components/shared/StatsChart';
import { Link } from 'react-router-dom'

import {
	formatUnixTimestamp,
	formatBalanceInSUI,
	formatBalanceInMIST
} from '@/utils/format';

import {
	Flag,
	Gauge,
	ArrowLeftRight,
	CircleUserRound,
	Box,
	Clock,
	Hash,
	DollarSign
} from "lucide-react";

import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/components/ui/card'
import {
	useNetworkStats,
	useNetworkThroughput,
	useNetworkAverageGasFee,
	useNetworkDailyTransactions,
	useNetworkDailyActiveAccounts,
	useNetworkDailyTransactionBlocks,
	useCheckpointRecents,
	useTransactionBlockRecents
} from '@/hooks/useApi';


export const OverviewView = () => {

	const { data: networkStats } = useNetworkStats()
	const { data: networkThroughput } = useNetworkThroughput()
	const { data: networkAverageGasFee } = useNetworkAverageGasFee()
	const { data: networkDailyActiveAccounts } = useNetworkDailyActiveAccounts()
	const { data: networkDailyTransactionBlocks } = useNetworkDailyTransactionBlocks()
	const { data: networkDailyTransactions } = useNetworkDailyTransactions()
	const { data: checkpointRecents } = useCheckpointRecents(4)
	const { data: transactionBlockRecents } = useTransactionBlockRecents(10, null, null);

	return (
		<div className='flex-1 overflow-y-auto overflow-x-hidden flex max-w-full 
  			[&::-webkit-scrollbar-track]:bg-gray-100
  			[&::-webkit-scrollbar-thumb]:bg-gray-300
  			dark:[&::-webkit-scrollbar-track]:bg-neutral-700
  			dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500'
		>
			<div className='flex flex-col gap-1 w-full h-full'>
				{/* Stat Cards */}
				<div
					className='grid grid-cols-1 gap-2 p-2
						md:grid-cols-2 md:px-4
						xl:grid-cols-4 xl:px-4
					'>
					<StatsCard
						title="Total Checkpoints"
						className='text-sm'
						valueStyleOverride='text-lg lg:text-2xl'
						tooltip='The total number of checkpoints produced by the network'
						value={Number(networkStats?.totalCheckpoints).toLocaleString()}
						icon={<Flag className="h-4 w-4" />}
					/>
					<StatsCard
						title="TPS"
						className='text-sm'
						valueStyleOverride='text-lg lg:text-2xl'
						tooltip='The number of transaction blocks processed by validators per second'
						value={networkThroughput?.ptbThroughput.toLocaleString()}
						icon={<Gauge className="h-4 w-4" />}
					/>
					<StatsCard
						title="Average Gas Fee"
						className='text-sm'
						valueStyleOverride='text-lg lg:text-2xl'
						tooltip='The average gas charge for processing transactions on the network (24h)'
						value={
							<>
								<div className='text-md'>
									{networkAverageGasFee?.averageGasFee ? formatBalanceInSUI(Number(networkAverageGasFee.averageGasFee)) : '--'} SUI
								</div>
								<div className='text-sm text-muted-foreground'>
									{networkAverageGasFee?.averageGasFee ? formatBalanceInMIST(Number(networkAverageGasFee.averageGasFee)) : '--'} MIST
								</div>
							</>
						}
						icon={<ArrowLeftRight className="h-4 w-4" />}
					/>
					<StatsCard
						title="Sui Price (USD)"
						className='text-sm'
						valueStyleOverride='text-lg lg:text-2xl'
						value={`$${networkStats?.marketData?.currentPrice}`}
						icon={<DollarSign className="h-4 w-4" />}
					/>
				</div>
				
				{/* Stat Charts */}
				<div
					className='grid grid-cols-1 gap-2 p-2
						md:grid-cols-3 md:px-4
					'
				>
					<StatsChart
						className='text-sm'
						descriptionStyleOverride='text-xs md:text-md'
						title='Daily Active Accounts (7 Day)'
						data={networkDailyActiveAccounts as any}
						xDataKey='DAY'
						yDataKey='ACTIVE_ACCOUNTS'
						icon={<CircleUserRound className='h-4 w-4' />}
						description='The total number of accounts with at least one transaction in a 24 hour period.'
					/>
					<StatsChart
						className='text-sm'
						descriptionStyleOverride='text-xs md:text-md'
						title='Daily Transaction Blocks (7 Day)'
						data={networkDailyTransactionBlocks as any}
						xDataKey='DAY'
						yDataKey='PTB_COUNT'
						icon={<Box className='h-4 w-4' />}
						description='The total number of transaction blocks produced in a 24 hour period.'
					/>
					<StatsChart
						className='text-sm'
						descriptionStyleOverride='text-xs md:text-md'
						title='Daily Transactions (7 Day)'
						data={networkDailyTransactions as any}
						xDataKey='DAY'
						yDataKey='TRANSACTION_COUNT'
						icon={<ArrowLeftRight className='h-4 w-4' />}
						description='The total number of transactions included in transaction blocks in a 24 hour period.'
					/>
				</div>
				<div 
					className='grid grid-cols-1 gap-2 p-2
						md:grid-cols-2 md:px-4
					'
				>
					{/* Recent Checkpoints */}
					<div className='flex-1'>
						<Card className='bg-transparent'>
							<CardHeader className="flex flex-col justify-start space-y-0 p-0 min-w-60">
								<div className='flex flex-row items-center px-2 pt-2'>
									<div className="rounded-full px-2 transition-colors">
										<Flag className='h-4 w-4' />
									</div>
									<div className='flex-1'>
										<CardTitle className="text-sm md:text-md lg:text-lg font-medium">Recent Checkpoints</CardTitle>
									</div>
									<div className='flex flex-col rounded-full p-2 transition-colors hover:bg-muted'>
										{/* <CircleHelp className='h-5 w-5' /> */}
									</div>
								</div>
								<div className='px-4'>
									<CardDescription className='text-xs md:text-sm lg:text-md'>
										Recently produced checkpoints
									</CardDescription>
								</div>

							</CardHeader>
							<CardContent className='pt-4'>
								<div className='flex flex-col gap-3'>
									{checkpointRecents?.data.map((checkpoint: any) => (
										<Card key={checkpoint.digest} className='bg-[#C0E6FF]/10 backdrop-blur-md border border-white/20 rounded-lg shadow-lg hover:bg-[#C0E6FF]/20'>
											<CardHeader className="flex flex-col justify-start space-y-0 p-0 min-w-60">
												<div className='flex flex-row items-center px-2 pt-2'>
													<div className="rounded-full px-2 transition-colors">
														<Box className='h-4 w-4' />
													</div>
													<div className='flex-1'>
														<CardTitle className='text-sm md:text-lg'>{checkpoint.sequenceNumber}</CardTitle>
													</div>
													<div className='flex flex-col rounded-full p-2 transition-colors hover:bg-muted'>
														{/* <CircleHelp className='h-5 w-5' /> */}
													</div>
												</div>

											</CardHeader>
											<CardContent className='space-y-0 p-0'>
												<div className='flex flex-col px-4 pt-2 pb-2 gap-1'>
													<Link to={`/checkpoints/${checkpoint.digest}`} className="flex items-center space-x-2">
														<span className='text-xs md:text-sm'>Digest:</span><span className='text-[#4DA2FF] hover:text-[#C0E6FF] text-xs'>{`${checkpoint.digest.slice(0, 6)}...${checkpoint.digest.slice(-6)}`}</span>
													</Link>
													<div className='flex items-center space-x-2'>
														<span className='whitespace-nowrap text-xs md:text-sm'>Timestamp: {formatUnixTimestamp(Number(checkpoint.timestampMS))}</span>
													</div>
												</div>
											</CardContent>
										</Card>
									))}
								</div>
							</CardContent>
						</Card>
					</div>

					{/* Latest Transaction Blocks */}
					<div className='flex-auto'>
						<Card className='bg-transparent'>
							<CardHeader className="flex flex-col justify-start space-y-0 p-0 min-w-60">
								<div className='flex flex-row items-center px-2 pt-2'>
									<div className="rounded-full px-2 transition-colors">
										<ArrowLeftRight className='h-5 w-5' />
									</div>
									<div className='flex-1'>
										<CardTitle className="text-sm md:text-md lg:text-lg font-medium">Recent Transaction Blocks</CardTitle>
									</div>
								</div>
								<div className='px-4'>
									<CardDescription className='text-xs md:text-sm lg:text-md'>
										Recently produced transaction blocks
									</CardDescription>
								</div>
							</CardHeader>
							<CardContent className='pt-4 px-2'>
								<div className='bg-[#C0E6FF]/10 backdrop-blur-md border border-white/20 rounded-md shadow-lg'>
									<Table>
										<TableHeader className='bg-[#C0E6FF]/10 rounded-t-lg shadow-lg'>
											<TableRow className='pointer-events-none'>
												<TableHead className='text-white'>
													<div className='flex flex-row items-center'>
														<div className='flex-1 text-xs font-medium'>Checkpoint</div>
													</div>
												</TableHead>
												<TableHead className='text-white'>
													<div className='flex flex-row items-center'>
														<div className='flex-1 text-xs font-medium'>Digest</div>
													</div>
												</TableHead>
												<TableHead className='text-white'>
													<div className='flex flex-row items-center'>
														<div className='flex-1 text-xs font-medium'>Timestamp</div>
													</div>
												</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{transactionBlockRecents?.data.map((transactionBlock: any) => (
												<TableRow key={transactionBlock.digest} className='hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'>
													<TableCell className='text-xs'>{Number(transactionBlock.checkpoint).toLocaleString()}</TableCell>
													<TableCell className='text-xs'>
														<Link to={`/transaction-blocks/${transactionBlock.digest}`} className="flex items-center space-x-2 text-[#4DA2FF] hover:text-[#C0E6FF]">
															<span className='hidden xl:block'>{`${transactionBlock.digest.slice(0, 6)}...${transactionBlock.digest.slice(-6)}`}</span>
															<span className='xl:hidden'>{`${transactionBlock.digest.slice(0, 6)}...`}</span>
														</Link>
													</TableCell>
													<TableCell className='text-xs'>{formatUnixTimestamp(Number(transactionBlock.timestampMS))}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</div>
							</CardContent>
						</Card>
					</div>
				</div>
			</div>
		</div>
	);
}